/**
 * React Query hooks for documents.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getProjectDocuments,
  getDocument,
  getDocumentView,
  createDocument,
  updateDocumentName,
  updateDocumentContent,
  updateDocumentMarkdown,
  deleteDocument,
  restoreDocument,
  hardDeleteDocument,
  processDocument,
  markDocumentReady,
  deleteTranscription,
  generateDocumentPdf,
  getDocumentGeneratedDocuments,
  deleteDocumentGeneratedDocument,
  getDocumentVersions,
  getDocumentVersion,
  createDocumentVersion,
  restoreDocumentVersion,
  type Document,
  type DocumentsResponse,
  type DocumentView,
  type DocumentVersionSummary,
  type DocumentVersionDetail,
  type ProjectGeneratedDocumentsResponse,
} from "../services/naraDoor";
import { getCurrentWorkspaceId } from "../lib/workspaceStore";

// Mock data for development mode
const DEV_MOCK_DOCUMENTS: Record<string, Document[]> = {
  "dev-project-1": [
    {
      id: "dev-doc-1",
      name: "Meeting Notes",
      project_id: "dev-project-1",
      status: "ready",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
    {
      id: "dev-doc-2",
      name: "Interview Transcript",
      project_id: "dev-project-1",
      status: "processing",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
    {
      id: "dev-doc-3",
      name: "Field Report",
      project_id: "dev-project-1",
      status: "ready",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
  ],
  "dev-project-2": [
    {
      id: "dev-doc-4",
      name: "Project Brief",
      project_id: "dev-project-2",
      status: "ready",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
  ],
};

export function useDocuments(projectId: string | undefined, options?: { deleted?: boolean }) {
  const workspaceId = getCurrentWorkspaceId();

  return useQuery<DocumentsResponse>({
    queryKey: ["documents", projectId, workspaceId, options?.deleted],
    queryFn: async () => {
      if (!projectId) {
        return { documents: [], total: 0 };
      }
      // In dev mode with mock project, return mock data
      if (import.meta.env.DEV && projectId.startsWith("dev-")) {
        const docs = DEV_MOCK_DOCUMENTS[projectId] || [];
        return { documents: docs, total: docs.length };
      }
      // workspaceId is auto-injected by callFunction
      const result = await getProjectDocuments(workspaceId!, projectId, 0, 100, options?.deleted ?? false);
      return result;
    },
    enabled: !!projectId && !!workspaceId,
  });
}

// Find a mock document by ID
function findMockDocument(documentId: string): Document | undefined {
  for (const docs of Object.values(DEV_MOCK_DOCUMENTS)) {
    const doc = docs.find((d) => d.id === documentId);
    if (doc) return doc;
  }
  return undefined;
}

export function useDocument(projectId: string | undefined, documentId: string | undefined) {
  const workspaceId = getCurrentWorkspaceId();

  return useQuery<Document>({
    queryKey: ["document", documentId, workspaceId],
    queryFn: async () => {
      if (!projectId || !documentId) {
        throw new Error("projectId and documentId are required");
      }
      // In dev mode with mock document, return mock data
      if (import.meta.env.DEV && documentId.startsWith("dev-")) {
        const doc = findMockDocument(documentId);
        if (doc) return doc;
      }
      // callRoute sends the current workspace
      return getDocument(projectId, documentId);
    },
    enabled: !!projectId && !!documentId && !!workspaceId,
    // Shorter staleTime for documents to ensure fresh status
    staleTime: 1000 * 10, // 10 seconds
    // Poll every 3 seconds while document is processing
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "processing" || status === "pending" || status === "awaiting_boosts" || status === "insufficient_boosts" ? 3000 : false;
    },
  });
}

export function useDocumentView(projectId: string | undefined, documentId: string | undefined, documentStatus?: string) {
  const workspaceId = getCurrentWorkspaceId();

  return useQuery<DocumentView>({
    queryKey: ["documentView", documentId, documentStatus, workspaceId],
    queryFn: async () => {
      if (!projectId || !documentId) {
        throw new Error("projectId and documentId are required");
      }
      // In dev mode with mock document, return mock data
      if (import.meta.env.DEV && documentId.startsWith("dev-")) {
        const doc = findMockDocument(documentId);
        if (doc) {
          return {
            id: doc.id,
            name: doc.name,
            content_json: {
              segments: [
                { id: "1", speaker: "Speaker 1", text: "This is a sample transcript segment." },
                { id: "2", speaker: "Speaker 2", text: "Here is another segment for testing." },
              ],
            },
          };
        }
      }
      // callRoute sends the current workspace
      return getDocumentView(projectId, documentId);
    },
    enabled: !!projectId && !!documentId && !!workspaceId,
    // Shorter staleTime for document view to ensure fresh content
    staleTime: 1000 * 10, // 10 seconds
    // Poll every 3 seconds while document is processing
    refetchInterval: documentStatus === "processing" || documentStatus === "pending" || documentStatus === "awaiting_boosts" || documentStatus === "insufficient_boosts" ? 3000 : false,
  });
}

export function useCreateDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      data,
    }: {
      projectId: string;
      data: {
        name: string;
        status?: string;
        files?: Array<{
          name: string;
          url?: string;
          mimeType?: string;
          size?: number;
        }>;
        metadata?: Record<string, unknown>;
      };
    }) => {
      return createDocument(projectId, data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["documents", variables.projectId],
      });
    },
  });
}

export function useUpdateDocumentName() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      documentId,
      name,
    }: {
      documentId: string;
      name: string;
      projectId: string;
    }) => {
      return updateDocumentName(projectId, documentId, name);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["documents", variables.projectId],
      });
      queryClient.invalidateQueries({
        queryKey: ["document", variables.documentId],
      });
    },
  });
}

export function useUpdateDocumentContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      documentId,
      contentJson,
    }: {
      projectId: string;
      documentId: string;
      contentJson: unknown;
    }) => {
      return updateDocumentContent(projectId, documentId, contentJson);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["document", variables.documentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["documentView", variables.documentId],
      });
    },
  });
}

export function useUpdateDocumentMarkdown() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      documentId,
      contentMd,
    }: {
      projectId: string;
      documentId: string;
      contentMd: string;
    }) => {
      return updateDocumentMarkdown(projectId, documentId, contentMd);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["document", variables.documentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["documentView", variables.documentId],
      });
    },
  });
}

export function useDeleteDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      documentId,
    }: {
      projectId: string;
      documentId: string;
    }) => {
      return deleteDocument(projectId, documentId);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["documents", variables.projectId],
      });
    },
  });
}

export function useRestoreDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      documentId,
    }: {
      projectId: string;
      documentId: string;
    }) => {
      return restoreDocument(projectId, documentId);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["documents", variables.projectId],
      });
    },
  });
}

export function useHardDeleteDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      documentId,
    }: {
      projectId: string;
      documentId: string;
    }) => {
      return hardDeleteDocument(projectId, documentId);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["documents", variables.projectId],
      });
    },
  });
}

export function useProcessDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      documentId,
      projectId,
    }: {
      documentId: string;
      projectId: string;
    }) => {
      return processDocument(projectId, documentId);
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ["document", variables.documentId] });
      queryClient.setQueriesData(
        { queryKey: ["document", variables.documentId] },
        (old: any) => old ? { ...old, status: "processing" } : old
      );
      queryClient.setQueriesData(
        { queryKey: ["documentView", variables.documentId] },
        (old: any) => old ? { ...old, status: "processing" } : old
      );
    },
    // Undo the optimistic "processing": a refused start (e.g. no Boosts) leaves the real status.
    onError: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["document", variables.documentId] });
      queryClient.invalidateQueries({ queryKey: ["documentView", variables.documentId] });
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch to get the updated status immediately
      queryClient.invalidateQueries({
        queryKey: ["document", variables.documentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["documentView", variables.documentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["documents", variables.projectId],
      });
      // Force immediate refetch
      queryClient.refetchQueries({
        queryKey: ["document", variables.documentId],
      });
    },
  });
}

export function useMarkDocumentReady() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      documentId,
      projectId,
    }: {
      documentId: string;
      projectId: string;
    }) => {
      return markDocumentReady(projectId, documentId);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["document", variables.documentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["documentView", variables.documentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["documents", variables.projectId],
      });
      queryClient.refetchQueries({
        queryKey: ["document", variables.documentId],
      });
    },
  });
}

export function useDeleteTranscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      documentId,
      projectId,
    }: {
      documentId: string;
      projectId: string;
    }) => {
      return deleteTranscription(projectId, documentId);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["document", variables.documentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["documentView", variables.documentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["documents", variables.projectId],
      });
      queryClient.refetchQueries({
        queryKey: ["document", variables.documentId],
      });
    },
  });
}

export function useGenerateDocumentPdf() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      documentId,
      recipeId,
    }: {
      projectId: string;
      documentId: string;
      recipeId?: string;
    }) => {
      return generateDocumentPdf(projectId, documentId, recipeId);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["documentView", variables.documentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["documentGeneratedDocuments", variables.documentId],
      });
    },
  });
}

export function useDocumentGeneratedDocuments(projectId: string | undefined, documentId: string | undefined) {
  const workspaceId = getCurrentWorkspaceId();

  return useQuery<ProjectGeneratedDocumentsResponse>({
    queryKey: ["documentGeneratedDocuments", documentId, workspaceId],
    queryFn: async () => {
      if (!projectId || !documentId) {
        return { documents: [], count: 0 };
      }
      return getDocumentGeneratedDocuments(projectId, documentId);
    },
    enabled: !!projectId && !!documentId && !!workspaceId,
    // Auto-refetch every 3 seconds while there are active (non-terminal) items
    refetchInterval: (query) => {
      const docs = query.state.data?.documents;
      if (docs?.some(d => d.status && !["success", "completed", "failed", "ready"].includes(d.status))) {
        return 3000;
      }
      return false;
    },
  });
}

export function useDeleteDocumentGeneratedDocument(projectId: string | undefined, documentId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      generatedDocumentId,
    }: {
      generatedDocumentId: string;
    }) => {
      if (!projectId || !documentId) throw new Error("projectId and documentId are required");
      return deleteDocumentGeneratedDocument(projectId, documentId, generatedDocumentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["documentGeneratedDocuments", documentId],
      });
    },
  });
}

// =====================================================
// Document Versions
// =====================================================

export function useDocumentVersions(projectId: string | undefined, documentId: string | undefined) {
  const workspaceId = getCurrentWorkspaceId();

  return useQuery<DocumentVersionSummary[]>({
    queryKey: ["documentVersions", documentId, workspaceId],
    queryFn: async () => {
      if (!projectId || !documentId) {
        return [];
      }
      return getDocumentVersions(projectId, documentId);
    },
    enabled: !!projectId && !!documentId && !!workspaceId,
  });
}

export function useDocumentVersion(projectId: string | undefined, documentId: string | undefined, versionId: string | undefined) {
  const workspaceId = getCurrentWorkspaceId();

  return useQuery<DocumentVersionDetail>({
    queryKey: ["documentVersion", documentId, versionId, workspaceId],
    queryFn: async () => {
      if (!projectId || !documentId || !versionId) {
        throw new Error("projectId, documentId and versionId are required");
      }
      return getDocumentVersion(projectId, documentId, versionId);
    },
    enabled: !!projectId && !!documentId && !!versionId && !!workspaceId,
  });
}

export function useCreateDocumentVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      documentId,
      label,
    }: {
      projectId: string;
      documentId: string;
      label?: string;
    }) => {
      return createDocumentVersion(projectId, documentId, label);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["documentVersions", variables.documentId],
      });
    },
  });
}

export function useRestoreDocumentVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      documentId,
      versionId,
    }: {
      projectId: string;
      documentId: string;
      versionId: string;
    }) => {
      return restoreDocumentVersion(projectId, documentId, versionId);
    },
    onSuccess: (_, variables) => {
      // Invalidate both the document and versions
      queryClient.invalidateQueries({
        queryKey: ["document", variables.documentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["documentView", variables.documentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["documentVersions", variables.documentId],
      });
    },
  });
}
